"use client";

import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Cell, Legend } from "recharts";
import { ChartCard } from "./chart-card";
import type { BreakdownEntry } from "@/types/analytics";

interface MethodBreakdownChartProps {
  entries: BreakdownEntry[];
  activeMethods: string[] | undefined;
  isLoading: boolean;
  onSelectMethod: (method: string) => void;
}

export function MethodBreakdownChart({ entries, activeMethods, isLoading, onSelectMethod }: MethodBreakdownChartProps) {
  const activeSet = new Set(activeMethods ?? []);

  return (
    <ChartCard
      title="Errors by HTTP method"
      description="Click a bar to filter the dashboard to that method."
      isLoading={isLoading}
      isEmpty={!isLoading && entries.length === 0}
      emptyMessage="No method errors in the current results."
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={entries} margin={{ bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
          <XAxis dataKey="key" tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} axisLine={{ stroke: "var(--border)" }} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} axisLine={false} tickLine={false} width={36} allowDecimals={false} />
          <Tooltip
            cursor={{ fill: "var(--accent)" }}
            contentStyle={{
              background: "var(--popover)",
              color: "var(--popover-foreground)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              fontSize: 12,
            }}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} formatter={() => "Errors"} />
          <Bar
            dataKey="totalErrors"
            name="Errors"
            radius={[4, 4, 0, 0]}
            cursor="pointer"
            onClick={(entry) => {
              const key = (entry as unknown as BreakdownEntry).key;
              if (key) onSelectMethod(key);
            }}
          >
            {entries.map((entry) => (
              <Cell
                key={entry.key}
                fill="var(--chart-4)"
                fillOpacity={activeSet.size === 0 || activeSet.has(entry.key) ? 1 : 0.35}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
